var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { useEffect, useState } from "react";
import { useQuery } from "react-query";
import { useOkapiKy } from "@folio/stripes/core";
import { resolvePublisherPrefix } from "../api/publisherPrefixService";
import { toErrorWithResponse } from "../utils/httpErrors";
/** Typing pause (ms) before the typed number is resolved on the server. */
export var RESOLVE_DEBOUNCE_MS = 300;
/** No prefix for the number, or one the user may not read (another publisher's, for a guarantor). */
var NO_PREFIX_STATUSES = [403, 404];
/**
 * A number can match a publisher prefix only once it holds the international
 * prefix (978 / 979) and at least 5 more digits — the shortest agency +
 * publisher prefix. Shorter input is never sent to the server.
 */
var RESOLVABLE_NUMBER = /^97[89]\d{5,}$/;
/**
 * Finds the publisher prefix a typed product number (ISBN/ISMN) belongs to.
 *
 * The backend resolves the normalized number against all prefixes
 * (`GET publisher-prefixes/resolve`), so the browser no longer downloads every
 * publisher prefix to match it locally. The lookup waits for a
 * {@link RESOLVE_DEBOUNCE_MS} pause in typing and starts only once the number
 * is long enough to contain a prefix; while the user keeps typing the last
 * resolved prefix stays shown. A prefix of another agency than the selected
 * one, no match (404) or a foreign prefix (403) resolves to `null`.
 *
 * @param agency selected registration agency (ISBN / ISMN)
 * @param productNumber the typed number, hyphens allowed
 * @returns the matching prefix or null
 */
export var useResolvedPublisherPrefix = function (agency, productNumber) {
    var _a, _b, _c;
    var ky = useOkapiKy();
    var normalized = productNumber.replace(/\D/g, "");
    var resolvable = !!agency && RESOLVABLE_NUMBER.test(normalized);
    var _d = useState(""), debounced = _d[0], setDebounced = _d[1];
    useEffect(function () {
        var timer = setTimeout(function () { return setDebounced(resolvable ? normalized : ""); }, RESOLVE_DEBOUNCE_MS);
        return function () { return clearTimeout(timer); };
    }, [normalized, resolvable]);
    var data = useQuery(["publisher-prefix-resolve", debounced], function () { return __awaiter(void 0, void 0, void 0, function () {
        var error_1, status_1;
        var _a, _b;
        return __generator(this, function (_c) {
            switch (_c.label) {
                case 0:
                    _c.trys.push([0, 2, , 3]);
                    return [4 /*yield*/, resolvePublisherPrefix(ky, debounced)];
                case 1: return [2 /*return*/, _c.sent()];
                case 2:
                    error_1 = _c.sent();
                    status_1 = (_b = (_a = toErrorWithResponse(error_1)) === null || _a === void 0 ? void 0 : _a.response) === null || _b === void 0 ? void 0 : _b.status;
                    if (status_1 !== undefined && NO_PREFIX_STATUSES.includes(status_1)) {
                        return [2 /*return*/, null];
                    }
                    throw error_1;
                case 3: return [2 /*return*/];
            }
        });
    }); }, {
        enabled: debounced.length > 0,
        // keep the last resolved prefix while the next keystroke is resolved
        keepPreviousData: true,
        staleTime: 30000,
        cacheTime: 5 * 60 * 1000,
        retry: false,
    }).data;
    if (!resolvable || !data) {
        return null;
    }
    var prefixAgency = (_c = (_b = (_a = data.publisherPrefixRange) === null || _a === void 0 ? void 0 : _a.agencyPrefix) === null || _b === void 0 ? void 0 : _b.internationalPrefix) === null || _c === void 0 ? void 0 : _c.agency;
    return prefixAgency === agency ? data : null;
};
